import React, { useCallback, useState } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import { Profile } from '@atlaskit/atlassian-navigation';
import { ButtonItem, LinkItem, MenuGroup, Section } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import {
	TeamsAppAnalyticsContext,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import { TeamsLinkItem } from '@atlaskit/teams-app-internal-navigation/teams-link-item';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	Anchor,
	AppNames,
	Avatar,
	getConfig,
	useAnalytics,
	useFeedbackDialog,
	usePrincipalUser,
} from '@atlassian/ptc-common';

import { useStandaloneFlagActions } from '../../../../../../controllers/flags';

function analyticsDetails(linkId: string) {
	return {
		source: AnalyticsEventSource.USER_MENU,
		actionSubject: 'userMenuItem',
		actionSubjectId: linkId,
		action: AnalyticsEventActions.CLICKED,
	};
}

interface AnchorItemProps {
	href: string;
	children: React.ReactNode;
	target?: string;
	onClick?: () => void;
}

const AnchorItem = (props: AnchorItemProps) => {
	const { href, target, ...otherProps } = props;

	return (
		<Anchor to={href} target={target}>
			<ButtonItem {...otherProps} />
		</Anchor>
	);
};

export const FeedbackButton = (): React.JSX.Element => {
	const [, { addFlag }] = useStandaloneFlagActions();

	const openFeedbackDialog = useFeedbackDialog({
		addFlag,
		source: AnalyticsEventSource.USER_MENU,
		appName: AppNames.STANDALONE_DIRECTORY,
		entrypoint: 'Teams - User profile page',
	});

	return (
		<ButtonItem onClick={openFeedbackDialog}>
			<FormattedMessage {...messages.giveFeedback} />
		</ButtonItem>
	);
};

export const UserContent = (): React.JSX.Element => {
	const [{ principalUserId, email }] = usePrincipalUser();
	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();

	const trackClickProfile = useCallback(() => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.userMenuItem.clicked.linkProfile', undefined);
		} else {
			analytics.pushUIEvent(analyticsDetails('linkProfile'));
		}
	}, [analytics, fireEvent]);

	const trackManageAccount = useCallback(() => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.userMenuItem.clicked.linkManageAccount', undefined);
		} else {
			analytics.pushUIEvent(analyticsDetails('linkManageAccount'));
		}
	}, [analytics, fireEvent]);

	const trackClickLogout = useCallback(() => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.userMenuItem.clicked.linkLogout', undefined);
		} else {
			analytics.pushUIEvent(analyticsDetails('linkLogout'));
		}
	}, [analytics, fireEvent]);

	const sessionSyncedManageProfileUrl = `${
		getConfig().atlassianAccountUrl
	}/login?prompt=none&application=no-cobranding&login_hint=${encodeURIComponent(
		email,
	)}&continue=${encodeURIComponent(
		`${getConfig().atlassianAccountUrl}/manage-profile?ref=profile&src=usermenu`,
	)}`;

	if (fg('ptc-react-router-dom-links-migration')) {
		return (
			<MenuGroup>
				<Section>
					<TeamsLinkItem
						href={`/${principalUserId}?ref=userMenu`}
						intent="navigation"
						onClick={trackClickProfile}
					>
						<FormattedMessage {...messages.yourProfile} />
					</TeamsLinkItem>
				</Section>
				<Section>
					<TeamsLinkItem
						href={sessionSyncedManageProfileUrl}
						intent="navigation"
						data-test-selector="menu-item-manage-profile"
						onClick={trackManageAccount}
					>
						<FormattedMessage {...messages.manageYourAccount} />
					</TeamsLinkItem>
					<FeedbackButton />
					<TeamsLinkItem
						href={`${
							getConfig().atlassianAccountUrl
						}/logout?continue=${encodeURIComponent(window.location.href)}`}
						intent="action"
						data-test-selector="menu-item-log-out"
						onClick={trackClickLogout}
					>
						<FormattedMessage {...messages.logout} />
					</TeamsLinkItem>
				</Section>
			</MenuGroup>
		);
	}

	return (
		<MenuGroup>
			<Section>
				<AnchorItem href={`/${principalUserId}?ref=userMenu`} onClick={trackClickProfile}>
					<FormattedMessage {...messages.yourProfile} />
				</AnchorItem>
			</Section>
			<Section>
				<LinkItem
					href={sessionSyncedManageProfileUrl}
					target="_blank"
					data-test-selector="menu-item-manage-profile"
					onClick={trackManageAccount}
				>
					<FormattedMessage {...messages.manageYourAccount} />
				</LinkItem>
				<FeedbackButton />
				<LinkItem
					href={`${
						getConfig().atlassianAccountUrl
					}/logout?continue=${encodeURIComponent(window.location.href)}`}
					data-test-selector="menu-item-log-out"
					onClick={trackClickLogout}
				>
					<FormattedMessage {...messages.logout} />
				</LinkItem>
			</Section>
		</MenuGroup>
	);
};

const UserMenuInner = () => {
	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();
	const [{ principalUserId }] = usePrincipalUser();

	const [isOpen, setOpen] = useState(false);

	const onOpen = useCallback(
		(isOpen: boolean) => {
			if (isOpen) {
				if (fg('ptc-enable-ptc-common-analytics-refactor')) {
					fireEvent('track.userMenu.opened', undefined);
				} else {
					analytics.pushTrackEvent({
						source: AnalyticsEventSource.USER_MENU,
						actionSubject: 'userMenu',
						action: AnalyticsEventActions.OPENED,
					});
				}
			}
			return !isOpen;
		},
		[analytics, fireEvent],
	);

	return (
		<Popup
			placement="bottom-start"
			content={UserContent}
			isOpen={isOpen}
			onClose={() => setOpen(false)}
			trigger={(triggerProps) => (
				<Profile
					icon={<Avatar accountId={principalUserId} size="small" />}
					onClick={() => setOpen(onOpen)}
					isSelected={isOpen}
					tooltip={<FormattedMessage {...messages.yourProfile} />}
					testId="user-menu-profile-button"
					{...triggerProps}
				/>
			)}
		/>
	);
};

const UserMenu = (): React.JSX.Element => {
	if (fg('ptc-enable-ptc-common-analytics-refactor')) {
		return (
			<TeamsAppAnalyticsContext data={{ source: 'userMenu' }}>
				<UserMenuInner />
			</TeamsAppAnalyticsContext>
		);
	}
	return <UserMenuInner />;
};

export const messages = defineMessages({
	yourProfile: {
		id: 'ptc-directory.user-menu.item.your-profile',
		defaultMessage: 'Your profile',
		description: "Link to the current user's profile page",
	},
	giveFeedback: {
		id: 'ptc-directory.user-menu.item.give-feedback',
		defaultMessage: 'Give feedback',
		description: 'Opens a dialog for the user to enter and send feedback',
	},
	manageYourAccount: {
		id: 'ptc-directory.user-menu.item.manage.your.account',
		defaultMessage: 'Manage your account',
		description: 'A link in user menu that takes the user to a page to manage their account',
	},
	logout: {
		id: 'ptc-directory.user-menu.item.logout',
		defaultMessage: 'Log out',
		description: 'Link that logs out the current user',
	},
});

export default UserMenu;
